import React, { useState, useEffect } from 'react';
import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import StatusIndicatorBadge from 'components/ui/StatusIndicatorBadge';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import BiodataDetailsView from 'components/BiodataDetailsView';
import ReviewStatsWidget from './components/ReviewStatsWidget';
import BulkActionPanel from './components/BulkActionPanel';
import FilterPanel from './components/FilterPanel';

const ClabUserBiodataReview = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedBiodata, setSelectedBiodata] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBiodataDetails, setSelectedBiodataDetails] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    country: '',
    skill: '',
    vdrType: '',
    recruiter: '',
    dateRange: { start: '', end: '' }
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Mock biodata data
  const mockBiodataList = [
    {
      id: 'BD001',
      workerName: 'Ahmad Rahman',
      nationality: 'Bangladesh',
      skillCategory: 'Construction Worker',
      vdrType: 'VDR',
      submissionDate: '2024-01-15',
      recruiterName: 'Global Recruitment Agency',
      recruiterId: 'REC001',
      status: 'pending',
      passportNumber: 'BD1234567',
      age: 28,
      experience: '5 years',
      documents: ['passport', 'medical', 'skillCert'],
      expiryDate: null,
      profileImage: 'https://randomuser.me/api/portraits/men/32.jpg',
      submittedBy: 'recruiter@global.com',
      priority: 'medium',
      duplicateAlert: false
    },
    {
      id: 'BD002',
      workerName: 'Rajesh Kumar',
      nationality: 'India',
      skillCategory: 'Electrician',
      vdrType: 'eQuotaSpecial',
      submissionDate: '2024-01-14',
      recruiterName: 'Asia Pacific Recruiters',
      recruiterId: 'REC002',
      status: 'pending',
      passportNumber: 'IN9876543',
      age: 32,
      experience: '8 years',
      documents: ['passport', 'medical', 'skillCert', 'experience'],
      expiryDate: null,
      profileImage: 'https://randomuser.me/api/portraits/men/45.jpg',
      submittedBy: 'hr@asiapacific.com',
      priority: 'high',
      duplicateAlert: true
    },
    {
      id: 'BD003',
      workerName: 'Maria Santos',
      nationality: 'Philippines',
      skillCategory: 'Domestic Helper',
      vdrType: 'VDR',
      submissionDate: '2024-01-13',
      recruiterName: 'Southeast Manpower',
      recruiterId: 'REC003',
      status: 'pending',
      passportNumber: 'PH5555555',
      age: 26,
      experience: '3 years',
      documents: ['passport', 'medical'],
      expiryDate: null,
      profileImage: 'https://randomuser.me/api/portraits/women/28.jpg',
      submittedBy: 'admin@semanpower.com',
      priority: 'low',
      duplicateAlert: false
    },
    {
      id: 'BD004',
      workerName: 'Chen Wei',
      nationality: 'China',
      skillCategory: 'Chef',
      vdrType: 'eQuotaSpecial',
      submissionDate: '2024-01-12',
      recruiterName: 'International Talent Hub',
      recruiterId: 'REC004',
      status: 'approved',
      passportNumber: 'CN7777777',
      age: 35,
      experience: '12 years',
      documents: ['passport', 'medical', 'skillCert', 'experience'],
      expiryDate: '2024-04-12',
      profileImage: 'https://randomuser.me/api/portraits/men/67.jpg',
      submittedBy: 'talent@ithub.com',
      priority: 'high',
      duplicateAlert: false
    },
    {
      id: 'BD005',
      workerName: 'Fatima Al-Zahra',
      nationality: 'Pakistan',
      skillCategory: 'Nurse',
      vdrType: 'VDR',
      submissionDate: '2024-01-11',
      recruiterName: 'Healthcare Recruitment Co',
      recruiterId: 'REC005',
      status: 'pending',
      passportNumber: 'PK3333333',
      age: 29,
      experience: '6 years',
      documents: ['passport', 'medical', 'skillCert', 'experience', 'license'],
      expiryDate: null,
      profileImage: 'https://randomuser.me/api/portraits/women/42.jpg',
      submittedBy: 'hr@healthcare.com',
      priority: 'high',
      duplicateAlert: false
    }
  ];

  // Filter biodata based on current filters
  const filteredBiodata = mockBiodataList.filter(biodata => {
    const matchesSearch = biodata.workerName.toLowerCase().includes(filters.search.toLowerCase()) ||
                         biodata.passportNumber.toLowerCase().includes(filters.search.toLowerCase());
    const matchesCountry = !filters.country || biodata.nationality === filters.country;
    const matchesSkill = !filters.skill || biodata.skillCategory === filters.skill;
    const matchesVDR = !filters.vdrType || biodata.vdrType === filters.vdrType;
    const matchesRecruiter = !filters.recruiter || biodata.recruiterName.toLowerCase().includes(filters.recruiter.toLowerCase());
    
    return matchesSearch && matchesCountry && matchesSkill && matchesVDR && matchesRecruiter;
  });

  // Pagination
  const totalPages = Math.ceil(filteredBiodata.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedBiodata = filteredBiodata.slice(startIndex, startIndex + itemsPerPage);

  const handleSelectBiodata = (biodataId) => {
    setSelectedBiodata(prev => 
      prev.includes(biodataId) 
        ? prev.filter(id => id !== biodataId)
        : [...prev, biodataId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBiodata.length === paginatedBiodata.length) {
      setSelectedBiodata([]);
    } else {
      setSelectedBiodata(paginatedBiodata.map(b => b.id));
    }
  };

  const handleViewDetails = (biodata) => {
    setSelectedBiodataDetails(biodata);
    setShowDetailsModal(true);
  };

  const handleApprove = (biodataId, reason = '') => {
    // Mock approval logic
  };

  const handleReject = (biodataId, reason) => {
    // Mock rejection logic
  };

  const handleBulkAction = (action, selectedIds, reason = '') => {
    setSelectedBiodata([]);
  };

  const getExpiryCountdown = (expiryDate) => {
    if (!expiryDate) return null;
    
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Expired';
    if (diffDays <= 7) return `${diffDays} days left`;
    return `${Math.floor(diffDays / 30)} months left`;
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar 
        userRole="clab" 
        userName="Sarah Johnson"
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      
      <RoleBasedSidebar 
        userRole="clab"
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main className={`transition-all duration-300 pt-16 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6">
          <BreadcrumbNavigation userRole="clab" />
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">Biodata Review</h1>
            <p className="text-text-secondary">Review and approve pending foreign worker applications</p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="xl:col-span-3 space-y-6">
              {/* Filter Panel */}
              <FilterPanel 
                filters={filters}
                onFiltersChange={setFilters}
              />

              {/* Bulk Action Panel */}
              {selectedBiodata.length > 0 && (
                <BulkActionPanel 
                  selectedCount={selectedBiodata.length}
                  onBulkAction={handleBulkAction}
                  selectedIds={selectedBiodata}
                />
              )}

              {/* Biodata Table */}
              <div className="card">
                <div className="p-6 border-b border-secondary-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-text-primary">
                      Pending Reviews ({filteredBiodata.length})
                    </h2>
                    <div className="flex items-center space-x-4">
                      <select 
                        className="input-field text-sm"
                        value={itemsPerPage}
                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      >
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary-50 border-b border-secondary-200">
                      <tr>
                        <th className="px-6 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedBiodata.length === paginatedBiodata.length && paginatedBiodata.length > 0}
                            onChange={handleSelectAll}
                            className="rounded border-secondary-300 text-primary focus:ring-primary"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                          Worker Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                          Skill & Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                          Submission
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-secondary-200">
                      {paginatedBiodata.map((biodata) => (
                        <tr key={biodata.id} className="hover:bg-secondary-50 transition-colors duration-150">
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedBiodata.includes(biodata.id)}
                              onChange={() => handleSelectBiodata(biodata.id)}
                              className="rounded border-secondary-300 text-primary focus:ring-primary"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary-100">
                                <Image 
                                  src={biodata.profileImage}
                                  alt={biodata.workerName}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div>
                                <div className="flex items-center space-x-2">
                                  <p className="text-sm font-medium text-text-primary">{biodata.workerName}</p>
                                  {biodata.duplicateAlert && (
                                    <Icon name="AlertTriangle" size={16} className="text-warning" />
                                  )}
                                </div>
                                <p className="text-xs text-text-secondary">{biodata.nationality}</p>
                                <p className="text-xs text-text-secondary">{biodata.passportNumber}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-text-primary">{biodata.skillCategory}</p>
                              <div className="flex items-center space-x-2 mt-1">
                                <StatusIndicatorBadge 
                                  status={biodata.vdrType === 'VDR' ? 'active' : 'high'} 
                                  size="sm"
                                />
                                <span className="text-xs text-text-secondary">{biodata.vdrType}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm text-text-primary">{biodata.submissionDate}</p>
                              <p className="text-xs text-text-secondary">{biodata.recruiterName}</p>
                              {biodata.expiryDate && (
                                <p className="text-xs text-warning font-medium mt-1">
                                  {getExpiryCountdown(biodata.expiryDate)}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col space-y-1">
                              <StatusIndicatorBadge status={biodata.status} size="sm" />
                              <StatusIndicatorBadge status={biodata.priority} size="sm" />
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleViewDetails(biodata)}
                                className="p-2 text-accent hover:bg-accent-50 rounded-md transition-colors duration-150"
                                title="View Details"
                              >
                                <Icon name="Eye" size={16} />
                              </button>
                              <button
                                onClick={() => handleApprove(biodata.id)}
                                className="p-2 text-success hover:bg-success-50 rounded-md transition-colors duration-150"
                                title="Approve"
                              >
                                <Icon name="CheckCircle" size={16} />
                              </button>
                              <button
                                onClick={() => handleReject(biodata.id, 'Manual rejection')}
                                className="p-2 text-error hover:bg-error-50 rounded-md transition-colors duration-150"
                                title="Reject"
                              >
                                <Icon name="XCircle" size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-secondary-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-text-secondary">
                        Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredBiodata.length)} of {filteredBiodata.length} results
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-sm border border-secondary-300 rounded-md hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-text-secondary">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 text-sm border border-secondary-300 rounded-md hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar Widget */}
            <div className="xl:col-span-1">
              <ReviewStatsWidget />
            </div>
          </div>
        </div>
      </main>

      {/* Biodata Details Modal */}
      {showDetailsModal && selectedBiodataDetails && (
        <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-secondary-200 flex items-center justify-between">
              <h2 className="text-xl font-bold">Biodata Review Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-secondary-600 hover:text-secondary-900"
              >
                Close
              </button>
            </div>
            <div className="p-6">
              <BiodataDetailsView biodata={selectedBiodataDetails} />
            </div>
            <div className="p-6 border-t border-secondary-200 flex items-center justify-end gap-3">
              <button
                onClick={() => handleReject(selectedBiodataDetails.id)}
                className="px-4 py-2 bg-error text-white rounded"
              >
                Reject
              </button>
              <button
                onClick={() => handleApprove(selectedBiodataDetails.id)}
                className="px-4 py-2 bg-success text-white rounded"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClabUserBiodataReview;